import { AddOutlined } from "@mui/icons-material";
import { Box, Button, Stack, Toolbar, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { BaseModalComponent } from "../common/BaseModalComponent";
import { CatalogManager } from "./components/CatalogManager";
import { ConditionalRelationManager } from "./components/ConditionalRelationManager";
import { EntityTreeNavigator } from "./components/EntityTreeNavigator";
import { RecordFormModal } from "./components/RecordFromModal";
import { RelationshipManager } from "./components/RelationshipManager";
import { buildCatalogCreateBody, buildCatalogPatchBody, buildContextQueryParams, buildInitialFormState } from "./helpers";
import { usePostMutation } from "./hooks/useSectorWorkflow.mutation";
import { useCatalogRecords } from "./hooks/useSectorWorkflow.query";
import { useSectorWorkflowDeleteItem } from "./hooks/useSectorWorkflowDeleteItem.mutation";
import { usePatchMutation } from "./hooks/useSectorWorkflowUpdate.mutation";
import {
  IContextSelectionMap,
  IContextSelectorDefinition,
  IFormState,
  ISelectorOption,
  IWorkflowBaseRecord,
  WorkflowEditorMode,
  workflowNodes,
} from "./interfaces";

export default function SectorWorkflowConfiguration() {
  const [selectedNodeKey, setSelectedNodeKey] = useState<string>("typeOfServices");
  const [selectedCatalogRecordUid, setSelectedCatalogRecordUid] = useState<string | null>(null);
  const [catalogPage, setCatalogPage] = useState<number>(1);
  const [catalogSearch, setCatalogSearch] = useState<string>("");
  const [catalogPageSize] = useState<number>(15);
  const [catalogAccumulatedRecords, setCatalogAccumulatedRecords] = useState<IWorkflowBaseRecord[]>([]);
  const [deletingRecordUid, setDeletingRecordUid] = useState<string | null>(null);
  const [updatingRecordUid, setUpdatingRecordUid] = useState<string | null>(null);
  const [createFormResetKey, setCreateFormResetKey] = useState<number>(0);

  const [contextSelection, setContextSelection] = useState<IContextSelectionMap>({
    serviceUid: "",
    areaUid: "",
  });

  const queryClient = useQueryClient();

  const selectedNode = useMemo(() => {
    return workflowNodes.find((node) => node.key === selectedNodeKey) ?? workflowNodes[0];
  }, [selectedNodeKey]);

  const serviceNode = useMemo(() => {
    return workflowNodes.find((node) => node.key === "typeOfServices");
  }, []);

  const areaNode = useMemo(() => {
    return workflowNodes.find((node) => node.key === "areas");
  }, []);

  const createInitialFormState = useMemo<IFormState>(() => {
    return buildInitialFormState(selectedNode.fields ?? []);
  }, [selectedNode.key, selectedNode.fields]);

  const hasCompleteContextSelection = (selectors: IContextSelectorDefinition[] = [], values: Record<string, string>): boolean => {
    if (!selectors.length) {
      return true;
    }

    return selectors.every((selector) => Boolean(values[selector.key]));
  };

  const selectorDependencyKey = useMemo(() => {
    if (!selectedNode.selectors?.length) {
      return "";
    }

    return selectedNode.selectors.map((selector) => contextSelection[selector.key] ?? "").join("|");
  }, [selectedNode.selectors, contextSelection]);

  const relationQueryParams = useMemo(() => {
    return buildContextQueryParams(selectedNode.selectors, contextSelection);
  }, [selectedNode.selectors, contextSelection]);

  const shouldFetchCatalog = selectedNode.mode === WorkflowEditorMode.Catalog;
  const shouldFetchRelation = selectedNode.mode === WorkflowEditorMode.Relation;
  const shouldFetchConditionalRelationBase = selectedNode.mode === WorkflowEditorMode.ConditionalRelation;

  const conditionalRelationRequiresCompleteContext = false;

  const isConditionalContextReady = hasCompleteContextSelection(selectedNode.selectors, contextSelection);

  const shouldFetchConditionalRelation =
    shouldFetchConditionalRelationBase && (conditionalRelationRequiresCompleteContext ? isConditionalContextReady : true);

  const {
    records: catalogRecords,
    page: catalogCurrentPage,
    totalPages: catalogTotalPages,
    isLoading: catalogLoading,
    isFetching: catalogFetching,
    error: catalogError,
  } = useCatalogRecords({
    selectedNode,
    queryParams: {
      page: catalogPage,
      pageSize: catalogPageSize,
      name: catalogSearch || undefined,
    },
    enabled: shouldFetchCatalog,
  });

  const {
    records: relationRecords,
    page: relationCurrentPage,
    totalPages: relationTotalPages,
    isLoading: relationLoading,
    isFetching: relationFetching,
    error: relationError,
    refetch: refetchRelationRecords,
  } = useCatalogRecords({
    selectedNode,
    queryParams: {
      page: catalogPage,
      pageSize: catalogPageSize,
      ...relationQueryParams,
    },
    enabled: shouldFetchRelation,
  });

  const {
    records: conditionalRelationRecords,
    page: conditionalRelationCurrentPage,
    totalPages: conditionalRelationTotalPages,
    isLoading: conditionalRelationLoading,
    isFetching: conditionalRelationFetching,
    error: conditionalRelationError,
    refetch: refetchConditionalRelationRecords,
  } = useCatalogRecords({
    selectedNode,
    queryParams: {
      page: catalogPage,
      pageSize: catalogPageSize,
      ...relationQueryParams,
    },
    enabled: shouldFetchConditionalRelation,
  });

  const { records: serviceRecords } = useCatalogRecords({
    selectedNode: serviceNode ?? workflowNodes[0],
    queryParams: {
      page: 1,
      pageSize: 100,
    },
    enabled: Boolean(serviceNode),
  });

  const { records: areaRecords } = useCatalogRecords({
    selectedNode: areaNode ?? workflowNodes[0],
    queryParams: {
      page: 1,
      pageSize: 100,
    },
    enabled: Boolean(areaNode),
  });

  const activeRecords =
    selectedNode.mode === WorkflowEditorMode.Catalog
      ? catalogAccumulatedRecords
      : selectedNode.mode === WorkflowEditorMode.Relation
        ? relationRecords
        : selectedNode.mode === WorkflowEditorMode.ConditionalRelation
          ? conditionalRelationRecords
          : [];

  const activeCurrentPage =
    selectedNode.mode === WorkflowEditorMode.Catalog
      ? catalogCurrentPage
      : selectedNode.mode === WorkflowEditorMode.Relation
        ? relationCurrentPage
        : selectedNode.mode === WorkflowEditorMode.ConditionalRelation
          ? conditionalRelationCurrentPage
          : 1;

  const activeTotalPages =
    selectedNode.mode === WorkflowEditorMode.Catalog
      ? catalogTotalPages
      : selectedNode.mode === WorkflowEditorMode.Relation
        ? relationTotalPages
        : selectedNode.mode === WorkflowEditorMode.ConditionalRelation
          ? conditionalRelationTotalPages
          : 1;

  const activeLoading =
    selectedNode.mode === WorkflowEditorMode.Catalog
      ? catalogLoading
      : selectedNode.mode === WorkflowEditorMode.Relation
        ? relationLoading
        : selectedNode.mode === WorkflowEditorMode.ConditionalRelation
          ? conditionalRelationLoading
          : false;

  const activeFetching =
    selectedNode.mode === WorkflowEditorMode.Catalog
      ? catalogFetching
      : selectedNode.mode === WorkflowEditorMode.Relation
        ? relationFetching
        : selectedNode.mode === WorkflowEditorMode.ConditionalRelation
          ? conditionalRelationFetching
          : false;

  const activeError =
    selectedNode.mode === WorkflowEditorMode.Catalog
      ? catalogError
      : selectedNode.mode === WorkflowEditorMode.Relation
        ? relationError
        : selectedNode.mode === WorkflowEditorMode.ConditionalRelation
          ? conditionalRelationError
          : null;

  const hasMoreRecords = activeCurrentPage < activeTotalPages;

  const serviceOptions = useMemo<ISelectorOption[]>(() => {
    return serviceRecords.map((service) => ({
      id: service.uid,
      label: service.name,
    }));
  }, [serviceRecords]);

  const areaOptions = useMemo<ISelectorOption[]>(() => {
    return areaRecords.map((area) => ({
      id: area.uid,
      label: area.name,
    }));
  }, [areaRecords]);

  const optionsMap = useMemo<Record<string, ISelectorOption[]>>(() => {
    return {
      serviceUid: serviceOptions,
      areaUid: areaOptions,
    };
  }, [serviceOptions, areaOptions]);

  const createRecordMutation = usePostMutation<IWorkflowBaseRecord, Record<string, unknown> | FormData>({
    mutationOptions: {
      onSuccess: async (createdRecord) => {
        if (selectedNode.mode === WorkflowEditorMode.Catalog) {
          setCatalogAccumulatedRecords((current) => [createdRecord, ...current]);
        }

        if (selectedNode.mode === WorkflowEditorMode.Relation) {
          await refetchRelationRecords();
        }

        setSelectedCatalogRecordUid(createdRecord.uid);
        setCreateFormResetKey((current) => current + 1);

        await queryClient.invalidateQueries({
          queryKey: ["catalog-records", selectedNode.requestConfig?.uri ?? ""],
        });
      },
    },
  });

  const deleteRecordMutation = useSectorWorkflowDeleteItem<void>({
    mutationOptions: {
      onError: (error) => {
        console.error("Error deleting record", error);
      },
      onSettled: () => {
        setDeletingRecordUid(null);
      },
    },
  });

  const updateRecordMutation = usePatchMutation<IWorkflowBaseRecord, Record<string, unknown> | FormData>({
    mutationOptions: {
      onError: (error) => {
        console.error("Error updating record", error);
      },
      onSettled: () => {
        setUpdatingRecordUid(null);
      },
    },
  });

  useEffect(() => {
    setCatalogPage(1);
    setCatalogAccumulatedRecords([]);
    setSelectedCatalogRecordUid(null);
  }, [selectedNode.key, catalogSearch, selectorDependencyKey]);

  useEffect(() => {
    if (selectedNode.mode !== WorkflowEditorMode.Catalog) {
      return;
    }

    setCatalogAccumulatedRecords((current) => {
      if (catalogPage === 1) {
        return catalogRecords;
      }

      const currentIds = new Set(current.map((record) => record.uid));
      const nextRecords = catalogRecords.filter((record) => !currentIds.has(record.uid));

      return [...current, ...nextRecords];
    });
  }, [catalogRecords, catalogPage, selectedNode.mode]);

  useEffect(() => {
    if (!activeRecords.length) {
      setSelectedCatalogRecordUid(null);
      return;
    }

    const exists = activeRecords.some((record) => record.uid === selectedCatalogRecordUid);

    if (!exists) {
      setSelectedCatalogRecordUid(activeRecords[0].uid);
    }
  }, [activeRecords, selectedCatalogRecordUid]);

  const handleNodeSelect = (nodeKey: string) => {
    setSelectedNodeKey(nodeKey);
    setCatalogPage(1);
    setCatalogSearch("");
    setCatalogAccumulatedRecords([]);
    setSelectedCatalogRecordUid(null);
    setContextSelection({
      serviceUid: "",
      areaUid: "",
    });
  };

  const handleContextChange = (key: string, value: string) => {
    setContextSelection((current) => {
      const nextState: IContextSelectionMap = {
        ...current,
        [key]: value,
      };

      if (key === "serviceUid") {
        nextState.areaUid = "";
      }

      return nextState;
    });

    setCatalogPage(1);
    setSelectedCatalogRecordUid(null);
  };

  const handleLoadMoreRecords = () => {
    if (activeFetching || !hasMoreRecords) {
      return;
    }

    setCatalogPage((current) => current + 1);
  };

  const handleCreateRecord = async (values: IFormState) => {
    const uri = selectedNode.requestConfig?.uri;

    if (!uri) {
      console.error("Missing requestConfig.uri for selected node");
      return;
    }

    const body = buildCatalogCreateBody(values, selectedNode.fields ?? []);
    const isMultipart = body instanceof FormData;

    await createRecordMutation.mutateAsync({
      uri,
      body,
      config: isMultipart
        ? {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        : undefined,
    });
  };

  const handleDeleteRecord = async (recordUid: string) => {
    const baseUri = selectedNode.requestConfig?.uri;

    if (!baseUri) {
      console.error("Missing requestConfig.uri for selected node");
      return;
    }

    setDeletingRecordUid(recordUid);

    await deleteRecordMutation.mutateAsync({
      uri: `${baseUri}/${recordUid}`,
    });

    if (selectedNode.mode === WorkflowEditorMode.Catalog) {
      setCatalogAccumulatedRecords((current) => current.filter((item) => item.uid !== recordUid));
    }

    if (selectedNode.mode === WorkflowEditorMode.Relation) {
      await refetchRelationRecords();
    }

    setSelectedCatalogRecordUid((current) => {
      return current === recordUid ? null : current;
    });

    await queryClient.invalidateQueries({
      queryKey: ["catalog-records", selectedNode.requestConfig?.uri ?? ""],
    });
  };

  const handleUpdateRecord = async (record: IWorkflowBaseRecord, initialValues: IFormState, values: IFormState) => {
    const baseUri = selectedNode.requestConfig?.uri;

    if (!baseUri) {
      throw new Error("Missing requestConfig.uri for selected node");
    }

    const payload = buildCatalogPatchBody({
      initialValues,
      currentValues: values,
    });

    if (!payload.hasChanges) {
      return;
    }

    setUpdatingRecordUid(record.uid);

    const updatedRecord = await updateRecordMutation.mutateAsync({
      uri: `${baseUri}/${record.uid}`,
      body: payload.body,
      config: payload.isMultipart
        ? {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        : undefined,
    });

    if (selectedNode.mode === WorkflowEditorMode.Catalog) {
      setCatalogAccumulatedRecords((current) =>
        current.map((item) => {
          if (item.uid !== record.uid) {
            return item;
          }

          return {
            ...item,
            ...updatedRecord,
          };
        }),
      );
    }

    if (selectedNode.mode === WorkflowEditorMode.Relation) {
      await refetchRelationRecords();
    }

    await queryClient.invalidateQueries({
      queryKey: ["catalog-records", selectedNode.requestConfig?.uri ?? ""],
    });
  };

  const showCreateButton = selectedNode.mode === WorkflowEditorMode.Catalog;

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "grey.100",
      }}
    >
      <EntityTreeNavigator nodes={workflowNodes} selectedKey={selectedNodeKey} onSelect={handleNodeSelect} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Toolbar
          sx={{
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              {selectedNode.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedNode.subtitle}
            </Typography>
          </Box>

          {showCreateButton ? (
            <Stack direction="row" spacing={1}>
              <BaseModalComponent
                modalKey={`Superadmin-SectorWorkflows-Create-${selectedNode.key}`}
                modalLabel={`Create ${selectedNode.title}`}
                defaultSizes={{ width: 520, height: 620 }}
                elementTrigger={
                  <Button variant="contained" startIcon={<AddOutlined />}>
                    New
                  </Button>
                }
              >
                <RecordFormModal
                  title={`Create ${selectedNode.title}`}
                  fields={selectedNode.fields}
                  initialFormState={createInitialFormState}
                  onSaved={handleCreateRecord}
                  isSaving={createRecordMutation.isPending}
                  resetKey={createFormResetKey}
                />
              </BaseModalComponent>
            </Stack>
          ) : null}
        </Toolbar>

        <Box
          sx={{
            p: 3,
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Stack
            spacing={2.5}
            sx={{
              height: "100%",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {selectedNode.mode === WorkflowEditorMode.Catalog ? (
              <CatalogManager
                title={selectedNode.title}
                records={activeRecords}
                selectedRecordUid={selectedCatalogRecordUid}
                onSelectRecord={setSelectedCatalogRecordUid}
                fields={selectedNode.fields}
                onEditSaved={handleUpdateRecord}
                loading={activeLoading && catalogPage === 1}
                isFetchingMore={activeFetching && catalogPage > 1}
                hasMore={hasMoreRecords}
                onLoadMore={handleLoadMoreRecords}
                error={activeError?.message ?? null}
                allowDelete={Boolean(selectedNode.allowDelete)}
                deletingRecordUid={deletingRecordUid}
                onDeleteRecord={handleDeleteRecord}
                nodeMode={selectedNode.mode}
                searchValue={catalogSearch}
                onSearchChange={setCatalogSearch}
                searchPlaceholder="Search by name..."
              />
            ) : null}

            {selectedNode.mode === WorkflowEditorMode.Relation ? (
              <RelationshipManager
                node={selectedNode}
                onSaved={async () => {
                  await refetchRelationRecords();
                }}
              />
            ) : null}

            {selectedNode.mode === WorkflowEditorMode.ConditionalRelation ? (
              <ConditionalRelationManager
                node={selectedNode}
                selectors={selectedNode.selectors}
                contextSelection={contextSelection}
                onContextChange={handleContextChange}
                optionsMap={optionsMap}
                records={conditionalRelationRecords}
                selectedRecordUid={selectedCatalogRecordUid}
                onSelectRecord={setSelectedCatalogRecordUid}
                loading={conditionalRelationLoading && catalogPage === 1}
                error={conditionalRelationError?.message ?? null}
                hasMore={conditionalRelationCurrentPage < conditionalRelationTotalPages}
                isFetchingMore={conditionalRelationFetching && catalogPage > 1}
                onLoadMore={handleLoadMoreRecords}
                onSaveChanges={async () => {
                  await refetchConditionalRelationRecords();
                }}
              />
            ) : null}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
